import { Grid, Stack, styled } from "@mui/material";
import {
  CartIcon,
  LgPackageIcon,
  LgTargetIcon,
  PackageIcon,
  ShipIcon,
  TargetIcon,
  UserGroupIcon,
} from "Assets/icons";
import {
  SmTextMd,
  StyledATag,
  XlTextSemiBold,
  XsText,
  XsTextMd,
} from "Components";
import { SVGProps, useMemo } from "react";
import { useIntl } from "react-intl";
import { PATHS } from "../../../constants/routes";
import useMediaBreakpoints from "hooks/useMediaBreakpoints";
import { useLanguageContext } from "Components/LanguageProvider";
import { COUNTRY_CODES } from "constants/data";
import { useCommonData } from "providers/common-data";
import CountUpNumber from "Components/animation/CountUpNumber";
import { GlobalIcon } from "Assets/icons/global.icon";
import { ALink } from "Components/link";

type StatisticType = {
  title: string | number;
  desc: string;
  url: string;
  icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  mobileIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

export const Statistics = () => {
  const t = useIntl();
  const { appConfigs } = useCommonData();

  const statistics = useMemo(() => {
    const { stat } = appConfigs || {};
    const {
      no_buyer = 0,
      no_country = 0,
      no_product = 0,
      no_rfq = 0,
      no_supplier = 0,
    } = stat || {};

    return [
      {
        title: no_buyer,
        desc: "buyers",
        url: PATHS.buyers,
        icon: CartIcon,
      },
      {
        title: no_supplier,
        desc: "suppliers",
        url: PATHS.suppliers,
        icon: ShipIcon,
      },
      {
        title: no_product,
        desc: "products",
        url: PATHS.products,
        icon: LgPackageIcon,
      },
      {
        title: no_rfq,
        desc: "buying_leads",
        url: PATHS.buyleads,
        icon: LgTargetIcon,
      },
      {
        title: no_country,
        desc: "countries",
        url: PATHS.buyleads,
        icon: GlobalIcon,
      },
    ];
  }, [appConfigs?.stat]);

  return (
    <StyledStatistics>
      <Grid spacing={{ xs: 1, md: 2 }} container>
        {statistics.map((statistic, i: number) => (
          <Grid item key={i} xs={4} md={12 / 5} className="box">
            <Stack spacing={1}>
              <statistic.icon width={40} height={40} />
              <XlTextSemiBold className="value">
                <CountUpNumber
                  targetNumber={Number(statistic.title)}
                  duration={750}
                />
                +
              </XlTextSemiBold>
              <ALink href={statistic.url} target="_blank">
                <SmTextMd className="label">
                  {t.formatMessage({ id: statistic.desc })}
                </SmTextMd>
              </ALink>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </StyledStatistics>
  );
};

export const StatisticsMobile = () => {
  const t = useIntl();
  const { language } = useLanguageContext();
  const { isMobile } = useMediaBreakpoints();
  const { appConfigs } = useCommonData();
  const { stat } = appConfigs || {};

  const statistics: StatisticType[] = useMemo(() => {
    const {
      no_buyer = 0,
      no_product = 0,
      no_rfq = 0,
      no_supplier = 0,
    } = stat || {};

    return [
      {
        title: no_product,
        desc: "products",
        url: PATHS.products,
        icon: PackageIcon,
      },
      {
        title: no_supplier,
        desc: "suppliers",
        url: PATHS.suppliers,
        icon: UserGroupIcon,
      },
      {
        title: no_buyer,
        desc: "buyers",
        url: PATHS.buyers,
        icon: UserGroupIcon,
      },
      {
        title: no_rfq,
        desc: "buying_leads",
        url: PATHS.buyleads,
        icon: TargetIcon,
      },
    ];
  }, [stat]);

  return (
    <StyledStatistics>
      <Grid spacing={{ xs: 1, md: 2 }} container>
        {statistics.map((statistic, i: number) => (
          <Grid item key={i} xs={3} md={12 / 5} className="box">
            <StyledATag href={statistic.url} target="_blank">
              <Stack spacing={1}>
                {statistic.icon && <statistic.icon width={24} height={24} />}
                <XsTextMd
                  className="label"
                  height={isMobile && language === COUNTRY_CODES.vi ? 36 : 18}
                >
                  {t.formatMessage({ id: statistic.desc })}
                </XsTextMd>
                <XsText className="value">
                  <CountUpNumber
                    targetNumber={Number(statistic.title)}
                    duration={750}
                  />
                  +
                </XsText>
              </Stack>
            </StyledATag>
          </Grid>
        ))}
      </Grid>
    </StyledStatistics>
  );
};

const StyledStatistics = styled(Stack)(({ theme }) => ({
  // backgroundColor: "white",
  padding: 8,
  ".box": {
    padding: 8,
    textAlign: "center",
    svg: {
      margin: "0 auto",
    },
    "& .value": {
      color: "var(--green-600)",
    },
    "& .label": {
      color: "var(--gray-800)",
    },
  },
  [theme.breakpoints.down("md")]: {
    ".box": {
      padding: 0,
    },
    "& .value": {
      fontSize: 16,
    },
    "& .label": {
      fontSize: 12,
    },
  },
}));
